import { useEffect, useState } from 'react';
import type { Person, Plan, PlanSummary, Visit } from '@ambit/shared';
import { api } from '../api.ts';

/** Hand-built days from the catalog, surfaced as a horizontal strip. */
export function FeaturedDays({ onOpen }: { onOpen: (plan: Plan) => void }) {
  const [plans, setPlans] = useState<PlanSummary[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listPlans({ curated: true })
      .then(setPlans)
      .catch(() => undefined);
  }, []);

  if (plans.length === 0) return null;

  return (
    <section aria-labelledby="featured-heading" className="mt-10">
      <h2 id="featured-heading" className="display text-xs uppercase tracking-widest text-bone-500">
        Featured days
      </h2>
      {/* Horizontal scroll with snap: reads as a shelf on a phone. */}
      <ul className="-mx-5 mt-3 flex snap-x snap-mandatory gap-3 overflow-x-auto px-5 pb-2">
        {plans.map((plan) => (
          <li key={plan.id} className="w-64 shrink-0 snap-start">
            <button
              type="button"
              disabled={busyId === plan.id}
              onClick={() => {
                setBusyId(plan.id);
                api
                  .getPlan(plan.id)
                  .then(onOpen)
                  .catch(() => undefined)
                  .finally(() => setBusyId(null));
              }}
              className="flex h-full w-full flex-col rounded-md bg-spruce-800 p-3 text-left transition-colors hover:border-blaze-400 disabled:opacity-50"
            >
              <div className="display text-base leading-tight text-bone-100">{plan.title}</div>
              {plan.note && <p className="mt-1 line-clamp-3 text-xs text-bone-500">{plan.note}</p>}
              <div className="numeric mt-2 flex gap-3 text-xs text-bone-500">
                <span>{plan.stopCount} stops</span>
                {/* Leading space: these are flex items inside the button, so
                    `gap` separates them on screen but not in the button's
                    accessible name, which was one run of "3 stops$125". */}
                <span>{plan.totalCost === 0 ? ' free' : ` $${plan.totalCost}`}</span>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * The visit log. Being able to say "we haven't used the shenandoah region since
 * October" is the reason this exists — a planner that forgets what you did is
 * half a product.
 */
export function BeenThere({
  people,
  me,
  hasSession,
  onPersonAdded,
  onNeedsSignIn,
}: {
  people: Person[];
  me: string | null;
  hasSession: boolean;
  onPersonAdded: (person: Person) => void;
  onNeedsSignIn: () => void;
}) {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    void api
      .listVisits()
      .then(setVisits)
      .catch(() => undefined);
  }, []);

  return (
    <section aria-labelledby="been-heading" className="mt-12 border-t border-spruce-800 pt-6">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="been-heading" className="display text-xs uppercase tracking-widest text-bone-500">
          Been there
        </h2>
        {/*
          Signed out this used to open the whole form — date, rating,
          attendees, notes — and fail only on submit, after the typing was
          done. Logging a day is not possible without a session at all, so
          asking here costs nothing and saves filling in a form that cannot be
          sent. Same moment-of-need prompt that saving a plan already used.
        */}
        <button
          type="button"
          onClick={() => (hasSession ? setOpen((v) => !v) : onNeedsSignIn())}
          className="text-sm text-blaze-400 underline decoration-dotted underline-offset-4 hover:text-blaze-300"
        >
          {open ? 'never mind' : 'log a day'}
        </button>
      </div>

      {open && (
        <LogVisit
          people={people}
          me={me}
          onPersonAdded={onPersonAdded}
          onLogged={(visit) => {
            setVisits((all) => [visit, ...all]);
            setOpen(false);
          }}
        />
      )}

      {visits.length === 0 ? (
        <p className="mt-3 text-sm text-bone-500">Nothing logged yet.</p>
      ) : (
        <ul className="mt-3 space-y-2">
          {visits.map((visit) => (
            <li key={visit.id} className="rounded-md bg-spruce-800 px-3 py-2.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-bone-100">
                  {visit.outings.map((o) => o.name).join(' · ')}
                </span>
                <span className="numeric shrink-0 text-xs text-bone-500">{visit.visitedOn}</span>
              </div>
              {visit.rating !== null && (
                <div className="numeric mt-1 text-xs text-blaze-400" aria-label={`${visit.rating} out of 5`}>
                  {'★'.repeat(visit.rating)}
                  <span className="text-spruce-600">{'★'.repeat(5 - visit.rating)}</span>
                </div>
              )}
              {visit.notes && <p className="mt-1 text-xs text-bone-500">{visit.notes}</p>}
              {visit.attendees.length > 0 && (
                <p className="mt-1 text-xs text-bone-500">
                  {visit.attendees.map((a) => a.name).join(', ')}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Logging is anchored on a saved plan — "we did this one" — because that is the
 * common case and it fills in the outings for you.
 */
function LogVisit({
  people,
  me,
  onLogged,
  onPersonAdded,
}: {
  people: Person[];
  me: string | null;
  onLogged: (visit: Visit) => void;
  onPersonAdded: (person: Person) => void;
}) {
  const [plans, setPlans] = useState<Array<{ id: string; title: string }>>([]);
  const [planId, setPlanId] = useState('');
  const [visitedOn, setVisitedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState('');
  const [attendees, setAttendees] = useState<string[]>(me ? [me] : []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .listPlans()
      .then((all) => setPlans(all.map((p) => ({ id: p.id, title: p.title }))))
      .catch(() => undefined);
  }, []);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const plan = await api.getPlan(planId);
      const visit = await api.recordVisit({
        planId,
        outingIds: plan.stops.map((s) => s.outing.id),
        visitedOn,
        rating: rating || null,
        notes: notes || null,
        attendees,
      });
      onLogged(visit);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="mt-3 rounded-md bg-spruce-800 p-4">
      <label htmlFor="plan" className="display block text-[10px] uppercase tracking-widest text-bone-500">
        Which day?
      </label>
      <select
        id="plan"
        required
        value={planId}
        onChange={(e) => setPlanId(e.target.value)}
        className="mt-1.5 w-full rounded-md bg-spruce-950 px-3 py-2 text-bone-100"
      >
        <option value="">Pick a saved plan…</option>
        {plans.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>

      <label htmlFor="when" className="display mt-3 block text-[10px] uppercase tracking-widest text-bone-500">
        When
      </label>
      <input
        id="when"
        type="date"
        required
        value={visitedOn}
        onChange={(e) => setVisitedOn(e.target.value)}
        className="numeric mt-1.5 w-full rounded-md bg-spruce-950 px-3 py-2 text-bone-100"
      />

      <fieldset className="mt-3">
        <legend className="display text-[10px] uppercase tracking-widest text-bone-500">How was it?</legend>
        <div className="mt-1.5 flex gap-1">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              aria-label={`${n} of 5`}
              aria-pressed={rating === n}
              onClick={() => setRating(n === rating ? 0 : n)}
              className={`px-1.5 text-xl leading-none transition-colors ${
                n <= rating ? 'text-blaze-400' : 'text-spruce-600 hover:text-bone-500'
              }`}
            >
              ★
            </button>
          ))}
        </div>
      </fieldset>

      <fieldset className="mt-3">
        <legend className="display text-[10px] uppercase tracking-widest text-bone-500">Who came</legend>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          {people.map((person) => {
            const on = attendees.includes(person.id);
            return (
              <button
                key={person.id}
                type="button"
                aria-pressed={on}
                onClick={() =>
                  setAttendees((all) =>
                    on ? all.filter((id) => id !== person.id) : [...all, person.id],
                  )
                }
                className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                  on
                    ? 'bg-blaze-500 font-semibold text-spruce-950'
                    : 'bg-spruce-700 text-bone-300 hover:bg-spruce-600'
                }`}
              >
                {person.name}
              </button>
            );
          })}
          {/*
            The roster was previously hidden entirely when empty, which is how
            the household ended up with a finished catalog and nobody in it.
            POST /api/people has existed the whole time with nothing calling it,
            so voting and attendance were gated behind a route with no button —
            invisible rather than broken, which is the worse of the two.
          */}
          <AddPerson onAdded={onPersonAdded} />
        </div>
        {people.length === 0 && (
          <p className="mt-1.5 text-xs text-bone-500">
            Nobody added yet. Votes and &ldquo;who came&rdquo; both need names.
          </p>
        )}
      </fieldset>

      <label htmlFor="notes" className="display mt-3 block text-[10px] uppercase tracking-widest text-bone-500">
        Notes <span className="normal-case tracking-normal text-bone-500">optional</span>
      </label>
      <textarea
        id="notes"
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        className="mt-1.5 w-full resize-y rounded-md bg-spruce-950 px-3 py-2 text-bone-100"
      />

      {error && (
        <p role="alert" className="mt-3 text-sm text-blaze-400">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !planId}
        className="mt-4 rounded-sm bg-blaze-500 px-4 py-2 text-sm font-semibold text-spruce-950 hover:bg-blaze-400 disabled:opacity-50"
      >
        {busy ? 'Saving…' : 'Log it'}
      </button>
    </form>
  );
}

/**
 * Adding someone, inline where the names are needed rather than behind a
 * settings screen. Five names entered once is the whole lifetime of this form,
 * so it collapses back to a link the moment it is done.
 */
export function AddPerson({ onAdded }: { onAdded: (person: Person) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [isAdult, setIsAdult] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-full border border-dashed border-spruce-600 px-3 py-1.5 text-sm text-bone-500 transition-colors hover:border-blaze-400 hover:text-blaze-300"
      >
        + someone
      </button>
    );
  }

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    api
      .createPerson({ name: trimmed, isAdult })
      .then((person) => {
        onAdded(person);
        setName('');
        setIsAdult(false);
        setOpen(false);
      })
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        // Enter submits, Escape abandons. This sits inside the visit form, so
        // a bare Enter would otherwise log the visit instead of adding the
        // person you are halfway through typing.
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            submit();
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        placeholder="Name"
        aria-label="Name of the person to add"
        className="w-28 rounded-full bg-spruce-950 px-3 py-1.5 text-sm text-bone-100"
      />
      <label className="flex items-center gap-1 text-xs text-bone-500">
        <input type="checkbox" checked={isAdult} onChange={(e) => setIsAdult(e.target.checked)} />
        adult
      </label>
      <button
        type="button"
        onClick={submit}
        disabled={busy || name.trim() === ''}
        className="rounded-full bg-blaze-500 px-3 py-1.5 text-sm font-semibold text-spruce-950 disabled:opacity-50"
      >
        Add
      </button>
    </span>
  );
}
